// Navbar.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const SunIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" fill="#000" viewBox="0 0 24 24" width="18" height="18">
    <path d="M6.995 12c0 2.761 2.246 5.005 5.005 5.005s5.005-2.244 5.005-5.005c0-2.758-2.246-5.005-5.005-5.005s-5.005 2.247-5.005 5.005zm13.005 1h3v-2h-3v2zm-18 0v-2h-3v2h3zm8.999-8.999v-3h-2v3h2zm0 18v-3h-2v3h2zm7.071-14.142l2.121-2.121-1.414-1.414-2.121 2.121 1.414 1.414zm-14.142 14.142l-2.121-2.121-1.414 1.414 2.121 2.121 1.414-1.414zm14.142 0l1.414-1.414-2.121-2.121-1.414 1.414 2.121 2.121zm-14.142-14.142l1.414-1.414-2.121-2.121-1.414 1.414 2.121 2.121z"/>
  </svg>
);

const MoonIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" fill="#000" viewBox="0 0 24 24" width="18" height="18">
    <path d="M21.752 15.002a9.718 9.718 0 0 1-4.002.835c-5.522 0-10-4.477-10-10 0-1.428.301-2.787.835-4.002a.5.5 0 0 0-.835-.549 10.969 10.969 0 0 0-2.75 7.051c0 6.075 4.925 11 11 11a10.97 10.97 0 0 0 7.051-2.75.5.5 0 0 0-.549-.835z"/>
  </svg>
);

export default function Navbar({ language, setLanguage, darkMode, setDarkMode }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const navRef = useRef(null);
  const logoRef = useRef(null);
  const menuRef = useRef(null);
  const hamburgerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setOpen(false);
      }
      updateWatermarkBounds();
    };

    window.addEventListener('resize', handleResize);
    const id = setInterval(updateWatermarkBounds, 400); // keep it updated while layout settles
    updateWatermarkBounds();

    return () => {
      window.removeEventListener('resize', handleResize);
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // compute & set CSS vars to keep watermark between logo and menu/hamburger
  const updateWatermarkBounds = () => {
    const nav = navRef.current;
    const logo = logoRef.current;
    const menu = menuRef.current;
    const hamburger = hamburgerRef.current;

    if (!nav || !logo) return;

    // left bound = right edge of logo + small gap
    const left = logo.offsetLeft + logo.offsetWidth + 12;

    // choose menu anchor depending on viewport (hamburger visible on mobile)
    let anchor = null;
    if (window.innerWidth <= 768 && hamburger) {
      anchor = hamburger;
    } else if (menu) {
      anchor = menu;
    }

    // default right bound (if no anchor) - keep watermark limited to 50% on large screens
    let right = Math.round(nav.offsetWidth / 2);

    if (anchor) {
      // distance from nav's right edge to anchor's left edge
      right = Math.round(nav.offsetWidth - anchor.offsetLeft + 8);
    }

    // clamp values so we don't produce negative positions
    const safeLeft = Math.max(12, Math.min(left, nav.offsetWidth - 60));
    const safeRight = Math.max(16, Math.min(right, nav.offsetWidth - safeLeft - 20));

    nav.style.setProperty('--wm-left', `${safeLeft}px`);
    nav.style.setProperty('--wm-right', `${safeRight}px`);
    // adjust watermark size on very small widths
    const wmSize = window.innerWidth <= 420 ? 'auto 55%' : window.innerWidth <= 768 ? 'auto 60%' : 'auto 100%';
    nav.style.setProperty('--wm-size', wmSize);
  };

  const linkStyle = {
    textDecoration: 'none',
    color: darkMode ? '#fff' : '#000',
    fontSize: '1rem',
    padding: '8px 12px',
    borderRadius: '6px',
    fontFamily: language === 'ar' ? "'Amiri', sans-serif" : "'Poppins', sans-serif",
    fontWeight: language === 'ar' ? 400 : 'normal',
    transition: 'all 0.3s ease',
  };

  const getActiveClass = (path) => (location.pathname === path ? 'active-link' : '');

  const blueGradient = "linear-gradient(to right, #6372ff 0%, #5ca9fb 100%)";
  const whiteGradient = "linear-gradient(to right, #ffffff, #f0f0f0)";

  const translations = {
    en: { home: 'Home', services: 'Services', about: 'About', contact: 'Contact' },
    ar: { home: 'الرئيسية', services: 'الخدمات', about: 'من نحن', contact: 'الاتصال' },
  };

  const handleLanguageSwitch = (lang) => {
    setLanguage(lang);
  };

  return (
    <>
      <nav
        ref={navRef}
        className="navbar"
        style={{
          background: darkMode
            ? "linear-gradient(rgba(18, 38, 126, 0.85), rgba(18, 38, 126, 1)), url('/images/navbar-bg.png')"
            : "#ffffff",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          border:darkMode?"0px":"1px solid skyblue",
          color: darkMode ? "#fff" : "#000",
          // provide sensible defaults (JS will override with --wm-left/right)
          '--wm-left': '160px',
          '--wm-right': '50%',
          '--wm-size': 'auto 100%',
        }}
      >
        <div className="navbar-brand" ref={logoRef}>
          {/* consistent logo size across pages; responsive below */}
          <img width={110} height={65} style={{ borderRadius: 10 }} src="/images/logo.png" alt="Logo" />
        </div>

        <div
          ref={hamburgerRef}
          className={`hamburger ${open ? 'open' : ''}`}
          onClick={() => { setOpen(!open); setTimeout(updateWatermarkBounds, 40); }}
          aria-label="Toggle Menu"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter') { setOpen(!open); setTimeout(updateWatermarkBounds, 40); }}}
        >
          <span></span>
          <span></span>
          <span></span>
        </div>

        <ul className="menu-desktop" ref={menuRef}>
          <li><Link to="/" style={linkStyle} className={getActiveClass('/')}>{translations[language].home}</Link></li>
          <li><Link to="/services" style={linkStyle} className={getActiveClass('/services')}>{translations[language].services}</Link></li>
          <li><Link to="/about" style={linkStyle} className={getActiveClass('/about')}>{translations[language].about}</Link></li>
          <li><Link to="/contact" style={linkStyle} className={getActiveClass('/contact')}>{translations[language].contact}</Link></li>
          <li>
            <div className="segmented-control">
              <button
                className={`segmented-button ${language === 'en' ? 'active' : ''}`}
                onClick={() => handleLanguageSwitch('en')}
                style={{ fontFamily: "'Poppins', sans-serif", width: '80px', padding: '6px 12px' }}
              >EN</button>
              <button
                className={`segmented-button arabic ${language === 'ar' ? 'active' : ''}`}
                onClick={() => handleLanguageSwitch('ar')}
                style={{ fontFamily: "'Amiri', sans-serif", fontWeight: 400, width: '80px', padding: '6px 12px' }}
              >العربية</button>
            </div>
          </li>
          <li>
            <label style={{ cursor: 'pointer' }}>
              <input type="checkbox" checked={darkMode} onChange={() => setDarkMode(!darkMode)} style={{ display: 'none' }} />
              <span
                className={`toggle-icon ${darkMode ? 'dark' : ''}`}
                style={{ background: darkMode ? whiteGradient : blueGradient }}
              >
                <span style={{ color: darkMode ? '#333' : '#fff' }}>
                  {darkMode ? MoonIcon : SunIcon}
                </span>
              </span>
            </label>
          </li>
        </ul>
      </nav>

      {open && (
        <ul className="menu-mobile">
          <li>
            <Link to="/" style={linkStyle} onClick={() => setOpen(false)}>
              {translations[language].home}
            </Link>
          </li>
          <li>
            <Link to="/services" style={linkStyle} onClick={() => setOpen(false)}>
              {translations[language].services}
            </Link>
          </li>
          <li>
            <Link to="/about" style={linkStyle} onClick={() => setOpen(false)}>
              {translations[language].about}
            </Link>
          </li>
          <li>
            <Link to="/contact" style={linkStyle} onClick={() => setOpen(false)}>
              {translations[language].contact}
            </Link>
          </li>
          <li>
            <div className="segmented-control">
              <button
                className={`segmented-button ${language === 'en' ? 'active' : ''}`}
                onClick={() => handleLanguageSwitch('en')}
              >
                EN
              </button>
              <button
                className={`segmented-button arabic ${language === 'ar' ? 'active' : ''}`}
                onClick={() => handleLanguageSwitch('ar')}
              >
                العربية
              </button>
            </div>
          </li>
          <li>
            <label style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={darkMode}
                onChange={() => setDarkMode(!darkMode)}
                style={{ display: 'none' }}
              />
              <span
                className={`toggle-icon ${darkMode ? 'dark' : ''}`}
                style={{ background: darkMode ? whiteGradient : blueGradient }}
              >
                <span style={{ color: darkMode ? '#333' : '#fff' }}>
                  {darkMode ? MoonIcon : SunIcon}
                </span>
              </span>
            </label>
          </li>
        </ul>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Amiri:wght@400&display=swap');

        .navbar {
          position: sticky;
          top: 0;
          z-index: 1000;
          padding: 12px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          backdrop-filter: blur(8px);

        }

        /* watermark uses CSS vars set by JS */
        .navbar::after {
          content: "";
          position: absolute;
          top: 0;
          bottom: 0;
          left: var(--wm-left);
          right: var(--wm-right);
          background-image: url('/images/home/infynix-watermark.png');
          background-repeat: no-repeat;
          background-size: var(--wm-size);
          background-position: center center;
          opacity: 0.28;
          pointer-events: none;
          z-index: 0;
        }

        .navbar * { position: relative; z-index: 1; }

        /* default logo size (consistent across pages) */
        .navbar-brand img { width: 110px; height: auto; border-radius: 10px; display: block; }

        .menu-desktop { display: flex; list-style: none; gap: 20px; margin: 0; padding: 0; align-items: center; }

        .hamburger { display: none; flex-direction: column; justify-content: space-between; width: 24px; height: 18px; cursor: pointer; }
        .hamburger span { height: 3px; background: ${darkMode ? '#fff' : '#000'}; border-radius: 2px; transition: all 0.3s ease; }
        .hamburger.open span:nth-child(1) { transform: rotate(45deg) translate(5px, 5px); }
        .hamburger.open span:nth-child(2) { opacity: 0; }
        .hamburger.open span:nth-child(3) { transform: rotate(-45deg) translate(5px, -5px); }

        .toggle-icon { width: 42px; height: 24px; border-radius: 30px; position: relative; display: inline-block; }
        .toggle-icon span { position: absolute; top: 3px; left: 3px; width: 18px; height: 18px; border-radius: 50%; display: flex; align-items: center; justify-content: center; transition: left 0.3s ease; }
        .toggle-icon.dark span { left: 22px; }

        .active-link { border-bottom: 2px solid #0044cc; font-weight: 600; }
        .active-link:hover { background: rgba(0, 68, 204, 0.05); }

        .menu-mobile { list-style: none; padding: 10px 20px; background: ${darkMode ? '#121212' : '#f9f9f9'}; display: flex; flex-direction: column; gap: 10px; }
        .menu-mobile li a { text-decoration: none; font-size: 1rem; color: ${darkMode ? '#fff' : '#000'}; padding: 10px 0; border-bottom: 1px solid ${darkMode ? '#333' : '#ccc'}; }

        .segmented-control { display: flex; border: 1px solid ${darkMode ? '#666' : '#ccc'}; border-radius: 8px; overflow: hidden; background: ${darkMode ? '#126' : '#bbb'}; }
        .segmented-button { flex: 1; padding: 6px 12px; background: transparent; border: none; cursor: pointer; font-size: 0.8rem; font-weight: bold; color: ${darkMode ? '#fff' : '#333'}; transition: all 0.3s ease; text-align: center; font-family: 'Poppins', sans-serif; width: 80px; }
        .segmented-button.arabic { font-family: 'Amiri', sans-serif; font-weight: 400; }
        .segmented-button.active { background: ${darkMode ? 'linear-gradient(to right, #6372ff 0%, #5ca9fb 100%)' : 'linear-gradient(to right, #ffffff, #f0f0f0)'}; color: ${darkMode ? '#fff' : '#000'}; font-weight: 800; }
        .segmented-button:hover:not(.active) { background: ${darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}; }
        .segmented-button:focus { outline: none; box-shadow: 0 0 0 2px ${darkMode ? '#5ca9fb' : '#6372ff'}; }

        @media (max-width: 768px) {
          .hamburger { display: flex; }
          .menu-desktop { display: none; }
          .navbar { padding: 10px 14px; }
          /* slightly smaller logo on small phones but consistent across pages */
          .navbar-brand img { width: 95px; }
        }

        body.dark-mode { background: #121212; color: #eee; }
      `}</style>
    </>
  );
}
